(function () {
  if (window.CustomThemeSections) {
    window.CustomThemeSections.init(document);
    return;
  }

  var initialized = new WeakSet();
  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function initVideos(root) {
    root.querySelectorAll('[data-observe-video]').forEach(function (section) {
      if (initialized.has(section)) return;
      var videos = section.querySelectorAll('video');
      if (!videos.length) return;
      initialized.add(section);
      var visibleVideos = new WeakSet();
      function updateVideo(video) {
        if (visibleVideos.has(video) && !document.hidden && !reducedMotion.matches && getComputedStyle(video).display !== 'none') {
          var playback = video.play();
          if (playback) playback.catch(function () {});
        } else {
          video.pause();
        }
      }
      function updateVideos() {
        videos.forEach(updateVideo);
      }
      var observer = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) visibleVideos.add(entry.target);
          else visibleVideos.delete(entry.target);
          updateVideo(entry.target);
        });
      }, { threshold: 0 });
      videos.forEach(function (video) {
        video.muted = true;
        video.playsInline = true;
        observer.observe(video);
      });
      document.addEventListener('visibilitychange', updateVideos);
      reducedMotion.addEventListener('change', updateVideos);
      var wrapper = section.closest('.shopify-section');
      if (wrapper) wrapper.addEventListener('shopify:section:unload', function () {
        observer.disconnect();
        document.removeEventListener('visibilitychange', updateVideos);
        reducedMotion.removeEventListener('change', updateVideos);
        videos.forEach(function (video) { video.pause(); });
        initialized.delete(section);
      }, { once: true });
    });
  }

  function initFocus(root) {
    root.querySelectorAll('[data-scroll-focus]').forEach(function (section) {
      if (initialized.has(section)) return;
      initialized.add(section);
      var scene = section.querySelector('.scroll-focus__scene');
      var cards = section.querySelectorAll('.scroll-focus__card');
      var focused = false;
      function updateGeometry() {
        var mobile = window.matchMedia('(max-width: 749px)').matches;
        var centerX = scene.clientWidth / 2;
        var centerY = scene.clientHeight / 2;
        var distance = Math.max(scene.clientWidth, scene.clientHeight) * 1.5;
        cards.forEach(function (card) {
          var dx = card.offsetLeft + (mobile ? 0 : card.offsetWidth / 2) - centerX;
          var dy = card.offsetTop + (mobile ? 0 : card.offsetHeight / 2) - centerY;
          if (dx === 0 && dy === 0) dy = -1;
          var magnitude = Math.max(1, Math.hypot(dx, dy));
          card.dataset.exitX = (dx / magnitude * distance).toFixed(1) + 'px';
          card.dataset.exitY = (dy / magnitude * distance).toFixed(1) + 'px';
        });
        setFocused(focused, true);
      }
      function setFocused(active, force) {
        if (active === focused && !force) return;
        focused = active;
        section.style.setProperty('--focus-progress', active ? '1' : '0');
        cards.forEach(function (card) {
          card.style.setProperty('--card-move-x', active ? card.dataset.exitX : '0px');
          card.style.setProperty('--card-move-y', active ? card.dataset.exitY : '0px');
          card.style.setProperty('--card-blur', active ? '5px' : '0px');
        });
      }
      var scheduled = false;
      function updateThreshold() {
        scheduled = false;
        // Reveal only once the bottom of the full image card enters the viewport.
        var bounds = scene.getBoundingClientRect();
        setFocused(bounds.bottom <= window.innerHeight);
      }
      function scheduleThresholdUpdate() {
        if (scheduled) return;
        scheduled = true;
        requestAnimationFrame(updateThreshold);
      }
      updateGeometry();
      updateThreshold();
      window.addEventListener('scroll', scheduleThresholdUpdate, { passive: true });
      window.addEventListener('resize', function () { updateGeometry(); scheduleThresholdUpdate(); });
      if (window.ResizeObserver) new ResizeObserver(function () {
        updateGeometry();
        scheduleThresholdUpdate();
      }).observe(scene);
    });
  }

  function initFolders(root) {
    root.querySelectorAll('[data-card-stack]').forEach(function (section) {
      if (initialized.has(section)) return;
      initialized.add(section);
      var cards = Array.prototype.slice.call(section.querySelectorAll('[data-card-stack-item]'));
      if (!cards.length) return;
      var active = 0;
      var timer;
      var visible = false;
      var viewport = section.querySelector('.card-stack__viewport');
      var nextButton = section.querySelector('[data-card-stack-item-next]');
      var swipeStartX = null;
      var suppressClick = false;
      var pointerInteraction = false;
      var hoverCapable = window.matchMedia('(hover: hover) and (pointer: fine)');
      function renderCards() {
        var mobile = window.matchMedia('(max-width: 749px)').matches;
        var visibleCount = mobile ? Math.min(cards.length, 5) : cards.length;
        var cardWidth = cards[0].offsetWidth;
        var cardHeight = cards[0].offsetHeight;
        // Lower the top edges more than the bottom edges, as in a fanned folder stack.
        var finalScale = visibleCount > 1 ? 0.70 : 1;
        var scaleStep = visibleCount > 1 ? (1 - finalScale) / (visibleCount - 1) : 0;
        var perspective = 1800;
        var geometry = cards.slice(0, visibleCount).map(function (_, slot) {
          var depth = visibleCount > 1 ? slot / (visibleCount - 1) : 0;
          var scale = 1 - slot * scaleStep;
          var degrees = 12 + depth * 14;
          var angle = degrees * Math.PI / 180;
          return {
            scale: scale,
            degrees: degrees,
            angle: angle,
            width: scale * cardWidth * Math.cos(angle) / (1 - cardWidth * Math.sin(angle) / perspective),
            y: (1 - scale) * cardHeight * 0.32
          };
        });
        var inset = mobile ? 8 : 12;
        var step = visibleCount > 1 ? Math.max(0, (viewport.clientWidth - inset * 2 - geometry[visibleCount - 1].width) / (visibleCount - 1)) : 0;
        cards.forEach(function (card, i) {
          var slot = (i - active + cards.length) % cards.length;
          var shown = slot < visibleCount;
          var poseIndex = Math.min(slot, visibleCount - 1);
          var pose = geometry[poseIndex];
          var x = inset + slot * step;
          var coveredProjection = poseIndex ? Math.max(0, (geometry[poseIndex - 1].width - step) / pose.scale) : 0;
          var coveredWidth = coveredProjection / (Math.cos(pose.angle) + coveredProjection * Math.sin(pose.angle) / perspective);
          // Keep every tab the same width; only its position follows the exposed edge.
          var tabWidthRatio = mobile ? 0.72 : 0.54;
          var tabLeft = Math.min(cardWidth * (1 - tabWidthRatio), Math.max(cardWidth * 0.11, coveredWidth + cardWidth * 0.025));
          // One transform value keeps rotation, scale and translation in sync in every state.
          card.style.setProperty('--stack-transform', 'translate3d(' + x.toFixed(2) + 'px,' + pose.y.toFixed(2) + 'px,0) scale(' + pose.scale.toFixed(4) + ') perspective(' + perspective + 'px) rotateY(-' + pose.degrees.toFixed(2) + 'deg)');
          card.style.setProperty('--tab-left', tabLeft.toFixed(1) + 'px');
          card.style.zIndex = String(cards.length - slot);
          if (!shown && card === document.activeElement) viewport.focus({ preventScroll: true });
          card.style.opacity = shown ? '1' : '0';
          card.style.visibility = shown ? 'visible' : 'hidden';
          card.style.pointerEvents = shown ? 'auto' : 'none';
          card.tabIndex = shown ? 0 : -1;
          card.setAttribute('aria-hidden', String(!shown));
          card.classList.toggle('is-active', slot === 0);
          card.setAttribute('aria-pressed', String(slot === 0));
        });
      }
      function select(index) {
        active = (index + cards.length) % cards.length;
        renderCards();
      }
      function stop() { clearInterval(timer); timer = null; }
      function start() {
        stop();
        var hovered = hoverCapable.matches && viewport.matches(':hover');
        var keyboardFocused = !pointerInteraction && viewport.contains(document.activeElement);
        if (visible && cards.length > 1 && !document.hidden && !reducedMotion.matches && !hovered && !keyboardFocused && swipeStartX === null) {
          timer = setInterval(function () { select(active + 1); }, Number(section.dataset.interval) || 5000);
        }
      }
      if (nextButton) {
        nextButton.hidden = cards.length < 2;
        nextButton.addEventListener('click', function () {
          if (suppressClick) return;
          select(active + 1);
          start();
        });
      }
      cards.forEach(function (card, i) {
        card.addEventListener('click', function () {
          if (suppressClick) return;
          select(i);
          start();
        });
        card.addEventListener('mouseenter', function () { if (hoverCapable.matches) stop(); });
        card.addEventListener('focus', function () { if (!pointerInteraction) stop(); });
      });
      viewport.addEventListener('pointerdown', function (event) {
        pointerInteraction = true;
        if (event.pointerType !== 'mouse') { swipeStartX = event.clientX; stop(); }
      });
      viewport.addEventListener('pointerup', function (event) {
        if (swipeStartX === null) return;
        var delta = event.clientX - swipeStartX;
        swipeStartX = null;
        if (Math.abs(delta) < 35) { start(); return; }
        suppressClick = true;
        setTimeout(function () { suppressClick = false; }, 400);
        select(active + (delta < 0 ? 1 : -1));
        start();
      });
      viewport.addEventListener('pointercancel', function () { swipeStartX = null; start(); });
      viewport.addEventListener('keydown', function (event) {
        pointerInteraction = false;
        stop();
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        select(active + (event.key === 'ArrowRight' ? 1 : -1));
        start();
      });
      viewport.addEventListener('mouseenter', function () { if (hoverCapable.matches) stop(); });
      viewport.addEventListener('mouseleave', start);
      viewport.addEventListener('focusin', function () { if (!pointerInteraction) stop(); });
      viewport.addEventListener('focusout', function (event) {
        if (!viewport.contains(event.relatedTarget)) requestAnimationFrame(start);
      });
      renderCards();
      window.addEventListener('resize', renderCards);
      document.addEventListener('visibilitychange', start);
      if (window.ResizeObserver) new ResizeObserver(renderCards).observe(viewport);
      var observer = new IntersectionObserver(function (entries) { visible = entries[0].isIntersecting; if (visible) start(); else stop(); }, { threshold: 0.2 });
      observer.observe(section);
    });
  }

  function initCompare(root) {
    root.querySelectorAll('[data-image-compare]').forEach(function (section) {
      if (initialized.has(section)) return;
      initialized.add(section);
      var range = section.querySelector('input[type="range"]');
      if (!range) return;
      range.addEventListener('input', function () { section.style.setProperty('--compare-position', range.value + '%'); });
      var copies = section.querySelectorAll('.image-compare__day-copy, .image-compare__night-copy');
      function alignCopy() {
        // Share a top edge while reserving room for the taller text column.
        var height = 0;
        copies.forEach(function (copy) { height = Math.max(height, copy.getBoundingClientRect().height); });
        section.style.setProperty('--compare-copy-height', height + 'px');
      }
      alignCopy();
      window.addEventListener('resize', alignCopy);
      if (window.ResizeObserver) {
        var copyObserver = new ResizeObserver(alignCopy);
        copies.forEach(function (copy) { copyObserver.observe(copy); });
      }
    });
  }

  function initSeries(root) {
    root.querySelectorAll('[data-product-showcase]').forEach(function (section) {
      if (initialized.has(section)) return;
      initialized.add(section);
      var banner = section.querySelector('.product-showcase__banner');
      if (!banner) return;
      var copies = section.querySelectorAll('.product-showcase__details');
      var frame = 0;
      function keepCopiesInside() {
        frame = 0;
        copies.forEach(function (copy) { copy.style.setProperty('--series-copy-shift-y', '0px'); });
        var bounds = banner.getBoundingClientRect();
        copies.forEach(function (copy) {
          var rect = copy.getBoundingClientRect();
          var shift = Math.max(bounds.top + 8 - rect.top, Math.min(0, bounds.bottom - 8 - rect.bottom));
          copy.style.setProperty('--series-copy-shift-y', shift + 'px');
        });
      }
      function schedule() {
        if (!frame) frame = requestAnimationFrame(keepCopiesInside);
      }
      schedule();
      window.addEventListener('resize', schedule);
      var observer;
      if (window.ResizeObserver) {
        observer = new ResizeObserver(schedule);
        observer.observe(banner);
        copies.forEach(function (copy) { observer.observe(copy); });
      }
      var wrapper = section.closest('.shopify-section');
      if (wrapper) wrapper.addEventListener('shopify:section:unload', function () {
        window.removeEventListener('resize', schedule);
        if (observer) observer.disconnect();
        if (frame) cancelAnimationFrame(frame);
      }, { once: true });
    });
  }

  function initDestinations(root) {
    root.querySelectorAll('[data-shipping-destinations]').forEach(function (container) {
      if (initialized.has(container)) return;
      var button = container.querySelector('[data-shipping-destinations-toggle]');
      var label = container.querySelector('[data-shipping-destinations-label]');
      var list = container.querySelector('.shipping-content__country-scroll');
      if (!button || !label || !list) return;
      initialized.add(container);
      container.dataset.expanded = 'false';
      button.hidden = false;
      button.addEventListener('click', function () {
        var expanded = button.getAttribute('aria-expanded') !== 'true';
        var buttonTop = button.getBoundingClientRect().top;
        container.dataset.expanded = String(expanded);
        button.setAttribute('aria-expanded', String(expanded));
        label.textContent = expanded ? button.dataset.collapseLabel : button.dataset.expandLabel;
        list.scrollTop = 0;
        // Keep the collapse control in view when the expanded list shrinks.
        if (!expanded) window.scrollBy(0, button.getBoundingClientRect().top - buttonTop);
      });
    });
  }

  function initPolicyWarranty(root) {
    root.querySelectorAll('[data-policy-content-warranty]').forEach(function (section) {
      if (initialized.has(section)) return;
      initialized.add(section);
      var mobile = window.matchMedia('(max-width: 749px)');
      var items = Array.prototype.slice.call(section.querySelectorAll('details'));
      var mobileStates = new WeakMap();
      items.forEach(function (item) {
        mobileStates.set(item, item.dataset.mobileOpen === 'true');
        item.querySelector('summary').addEventListener('click', function (event) {
          if (!mobile.matches) event.preventDefault();
        });
        item.addEventListener('toggle', function () {
          if (mobile.matches) mobileStates.set(item, item.open);
        });
      });
      function updateLayout() {
        items.forEach(function (item) {
          item.open = mobile.matches ? mobileStates.get(item) : true;
          item.querySelector('summary').tabIndex = mobile.matches ? 0 : -1;
        });
      }
      updateLayout();
      mobile.addEventListener('change', updateLayout);
      var wrapper = section.closest('.shopify-section');
      if (wrapper) wrapper.addEventListener('shopify:section:unload', function () {
        mobile.removeEventListener('change', updateLayout);
      }, { once: true });
    });
  }

  document.addEventListener('shopify:block:select', function (event) {
    var item = event.target.closest('.policy-content-warranty__item');
    if (item) item.open = true;
    else {
      var warranty = event.target.querySelector('[data-policy-content-warranty]');
      if (warranty) warranty.querySelectorAll('details').forEach(function (detail) { detail.open = true; });
    }
  });

  function init(root) {
    initVideos(root);
    initFocus(root);
    initFolders(root);
    initCompare(root);
    initSeries(root);
    initDestinations(root);
    initPolicyWarranty(root);
  }
  window.CustomThemeSections = { init: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(document); });
  else init(document);
  document.addEventListener('shopify:section:load', function (event) { init(event.target); });
})();

// Product scene carousel.
(() => {
  if (customElements.get('product-scenes')) return;

  class ProductSceneSlider extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      // Shared scripts can register this element before its children are parsed.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.abortController.signal });
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      const signal = this.abortController.signal;
      this.slides = Array.from(this.querySelectorAll('[data-scene-slide]'));
      this.navButtons = Array.from(this.querySelectorAll('[data-scene-nav]'));
      this.progressButtons = Array.from(this.querySelectorAll('[data-scene-progress]'));
      this.stage = this.querySelector('[data-scene-stage]');
      this.navigation = this.querySelector('[data-scene-navigation]');
      this.status = this.querySelector('[data-scene-status]');
      if (!this.stage || !this.slides.length) return;
      this.initialized = true;

      this.activeIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.dataset.autoplay !== 'true' || this.motionQuery.matches;
      this.hoverPaused = false;
      this.focusPaused = false;
      this.editorPaused = false;
      this.inViewport = !('IntersectionObserver' in window);
      this.pointerStart = null;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-scene-nav], [data-scene-progress]');
        if (button && this.contains(button)) {
          const index = Number(button.dataset.sceneNav ?? button.dataset.sceneProgress);
          this.selectScene(index, true);
        }
        const arrow = event.target.closest('[data-scene-step]');
        if (arrow && this.contains(arrow)) this.selectScene(this.activeIndex + Number(arrow.dataset.sceneStep), true);
      }, { signal });

      this.addEventListener('keydown', (event) => {
        const button = event.target.closest('[data-scene-nav], [data-scene-progress]');
        if (!button || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        let index = this.activeIndex + (event.key === 'ArrowLeft' ? -1 : 1);
        if (event.key === 'Home') index = 0;
        if (event.key === 'End') index = this.slides.length - 1;
        this.selectScene(index, true);
        const buttons = button.hasAttribute('data-scene-nav') ? this.navButtons : this.progressButtons;
        buttons[this.activeIndex]?.focus({ preventScroll: true });
      }, { signal });

      this.stage.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hoverPaused = true;
        this.schedule();
      }, { signal });
      this.stage.addEventListener('pointerleave', () => {
        this.hoverPaused = false;
        this.schedule();
      }, { signal });
      this.addEventListener('focusin', () => {
        this.focusPaused = true;
        this.schedule();
      }, { signal });
      this.addEventListener('focusout', (event) => {
        if (this.contains(event.relatedTarget)) return;
        this.focusPaused = false;
        this.schedule();
      }, { signal });

      this.stage.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' || event.target.closest('button')) return;
        this.pointerStart = { x: event.clientX, y: event.clientY, time: Date.now() };
      }, { signal });
      this.stage.addEventListener('pointerup', (event) => {
        if (!this.pointerStart) return;
        const dx = event.clientX - this.pointerStart.x;
        const dy = event.clientY - this.pointerStart.y;
        const duration = Date.now() - this.pointerStart.time;
        this.pointerStart = null;
        if (duration < 1200 && Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.2) {
          this.selectScene(this.activeIndex + (dx < 0 ? 1 : -1), true);
        }
      }, { signal });
      this.stage.addEventListener('pointercancel', () => { this.pointerStart = null; }, { signal });
      document.addEventListener('visibilitychange', () => this.schedule(), { signal });

      document.addEventListener('shopify:block:select', (event) => {
        const index = this.slides.findIndex((slide) => slide.dataset.blockId === event.detail?.blockId);
        if (index < 0) return;
        this.editorPaused = true;
        this.selectScene(index, false);
      }, { signal });
      document.addEventListener('shopify:block:deselect', (event) => {
        if (!this.slides.some((slide) => slide.dataset.blockId === event.detail?.blockId)) return;
        this.editorPaused = false;
        this.schedule();
      }, { signal });
      document.addEventListener('shopify:section:unload', (event) => {
        if (event.detail?.sectionId === this.dataset.sectionId) this.disconnectedCallback();
      }, { signal });

      this.onMotionChange = () => {
        if (this.motionQuery.matches) this.userPaused = true;
        this.schedule();
      };
      if (this.motionQuery.addEventListener) this.motionQuery.addEventListener('change', this.onMotionChange);
      else this.motionQuery.addListener(this.onMotionChange);

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inViewport = entries[0].isIntersecting;
          this.schedule();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      }
      if ('ResizeObserver' in window) {
        this.navigationObserver = new ResizeObserver(() => this.syncNavigation());
        this.navigationObserver.observe(this.navigation);
      }
      this.selectScene(this.activeIndex, false);
    }

    selectScene(index, announce) {
      this.activeIndex = (index + this.slides.length) % this.slides.length;
      this.slides.forEach((slide, position) => {
        const active = position === this.activeIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.toggleAttribute('inert', !active);
      });
      [...this.navButtons, ...this.progressButtons].forEach((button) => {
        const active = Number(button.dataset.sceneNav ?? button.dataset.sceneProgress) === this.activeIndex;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
      const slide = this.slides[this.activeIndex];
      this.stage.style.setProperty('--scene-active-ratio-desktop', slide.dataset.ratioDesktop);
      this.stage.style.setProperty('--scene-active-ratio-mobile', slide.dataset.ratioMobile);
      this.syncNavigation();
      if (announce && this.status) this.status.textContent = `${this.activeIndex + 1} of ${this.slides.length}: ${slide.dataset.label}`;
      this.schedule();
    }

    syncNavigation() {
      if (!this.navigation) return;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const page = mobile ? Math.floor(this.activeIndex / 4) : 0;
      this.navigation.scrollTo({ left: page * this.navigation.clientWidth, behavior: 'auto' });
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || !this.initialized || !this.abortController || this.slides.length < 2 || this.userPaused || this.hoverPaused || this.focusPaused || this.editorPaused || document.hidden || !this.inViewport) return;
      this.timer = setTimeout(() => this.selectScene(this.activeIndex + 1, false), 2000);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      this.navigationObserver?.disconnect();
      this.abortController?.abort();
      if (this.motionQuery && this.onMotionChange) {
        if (this.motionQuery.removeEventListener) this.motionQuery.removeEventListener('change', this.onMotionChange);
        else this.motionQuery.removeListener(this.onMotionChange);
      }
      this.abortController = null;
      this.initialized = false;
    }
  }

  customElements.define('product-scenes', ProductSceneSlider);
})();
// Native details keep specifications usable before JavaScript loads.
(() => {
  if (customElements.get('product-specifications')) return;

  class ProductSpecifications extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true, signal: this.abortController.signal });
      } else {
        this.initialize();
      }
    }

    initialize() {
      if (!this.isConnected || this.initialized) return;
      this.initialized = true;
      this.animations = new Map();
      const signal = this.abortController.signal;
      this.groups = Array.from(this.querySelectorAll('[data-specification-group]'));
      this.groups.forEach((group) => {
        const summary = group.querySelector('summary');
        summary.setAttribute('aria-expanded', String(group.open));
        summary.addEventListener('click', (event) => {
          event.preventDefault();
          this.setExpanded(group, summary.getAttribute('aria-expanded') !== 'true');
        }, { signal });
        group.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !group.open) return;
          event.preventDefault();
          this.setExpanded(group, false);
          summary.focus({ preventScroll: true });
        }, { signal });
      });
      document.addEventListener('shopify:block:select', (event) => {
        const group = this.groups.find((item) => item.dataset.blockId === event.detail?.blockId);
        if (group) this.setExpanded(group, true, false);
      }, { signal });
    }

    setExpanded(group, expanded, animate = true) {
      const summary = group.querySelector('summary');
      const body = group.querySelector('.product-specifications__body');
      const startHeight = group.getBoundingClientRect().height;
      this.animations.get(group)?.cancel();
      this.animations.delete(group);
      summary.setAttribute('aria-expanded', String(expanded));
      group.classList.toggle('is-closing', !expanded);
      if (!animate || !group.animate || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        group.open = expanded;
        group.classList.remove('is-closing');
        return;
      }
      group.open = true;
      const endHeight = summary.getBoundingClientRect().height + (expanded ? body.getBoundingClientRect().height : 0);
      const animation = group.animate([
        { height: `${startHeight}px` },
        { height: `${endHeight}px` }
      ], { duration: 250, easing: 'ease-in-out' });
      this.animations.set(group, animation);
      animation.onfinish = () => {
        group.open = expanded;
        group.classList.remove('is-closing');
        this.animations.delete(group);
      };
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.animations?.forEach((animation, group) => {
        animation.cancel();
        group.open = group.querySelector('summary').getAttribute('aria-expanded') === 'true';
        group.classList.remove('is-closing');
      });
      this.animations?.clear();
      this.abortController = null;
      this.initialized = false;
    }
  }

  customElements.define('product-specifications', ProductSpecifications);
})();

// Accessories have independent variants and quantities, but share the main product cart submission.
(() => {
  if (customElements.get('product-accessories')) return;

  class ProductAccessories extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      const initialize = () => queueMicrotask(() => this.initialize());
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true, signal: this.abortController.signal });
      } else {
        initialize();
      }
    }

    initialize() {
      if (!this.isConnected || !this.abortController || this.initialized) return;
      this.initialized = true;
      this.paymentButtons = new Map();
      this.rows = Array.from(this.querySelectorAll('[data-accessory-product]')).map((element) => {
        let variants;
        try {
          variants = JSON.parse(element.querySelector('[data-accessory-variants]').textContent);
        } catch (_) {
          variants = [];
        }
        const input = element.querySelector('[data-accessory-quantity]');
        const checked = element.querySelector('[data-accessory-variant]:checked');
        const variant = variants.find((item) => String(item.id) === checked?.value)
          || variants.find((item) => item.available) || variants[0];
        return { element, variants, input, variant };
      }).filter((row) => row.input);
      this.total = this.querySelector('[data-accessories-total]');
      const signal = this.abortController.signal;
      this.addEventListener('change', (event) => this.onChange(event), { signal });
      this.addEventListener('input', (event) => {
        if (event.target.matches('[data-accessory-quantity]')) this.updateSummary();
      }, { signal });
      this.addEventListener('click', (event) => this.onQuantityClick(event), { signal });
      this.rows.forEach((row) => this.renderRow(row));
      const productInfo = this.closest('product-info');
      if (productInfo) {
        // Shopify can insert accelerated checkout markup after this component initializes.
        this.paymentObserver = new MutationObserver(() => this.updatePaymentButtons());
        this.paymentObserver.observe(productInfo, { childList: true, subtree: true });
      }
      this.updateSummary();
    }

    getRules(row) {
      const min = Math.max(1, Math.floor(Number(row.variant?.min) || 1));
      const increment = Math.max(1, Math.floor(Number(row.variant?.increment) || 1));
      const suppliedMax = row.variant?.max;
      const max = suppliedMax == null || suppliedMax === ''
        ? Infinity : Math.max(0, Math.floor(Number(suppliedMax) || 0));
      return { min, increment, max };
    }

    normalizeQuantity(row, value) {
      if (!row.variant?.available) return 0;
      const { min, increment, max } = this.getRules(row);
      const requested = Number(value);
      if (!Number.isFinite(requested) || requested <= 0 || max < min) return 0;
      const quantity = min + Math.ceil((Math.max(min, Math.floor(requested)) - min) / increment) * increment;
      const largest = max === Infinity ? Infinity : min + Math.floor((max - min) / increment) * increment;
      return Math.min(quantity, largest);
    }

    onChange(event) {
      const row = this.rows.find((item) => item.element.contains(event.target));
      if (!row) return;
      if (event.target.matches('[data-accessory-variant]')) {
        const variant = row.variants.find((item) => String(item.id) === event.target.value);
        if (!variant) return;
        row.variant = variant;
      } else if (!event.target.matches('[data-accessory-quantity]')) {
        return;
      }
      this.renderRow(row);
      this.updateSummary();
    }

    onQuantityClick(event) {
      const button = event.target.closest('[data-accessory-decrease], [data-accessory-increase]');
      if (!button || !this.contains(button) || button.disabled) return;
      const row = this.rows.find((item) => item.element.contains(button));
      if (!row) return;
      event.preventDefault();
      const { min, increment } = this.getRules(row);
      const quantity = this.normalizeQuantity(row, row.input.value);
      if (button.hasAttribute('data-accessory-increase')) {
        row.input.value = quantity === 0 ? min : quantity + increment;
      } else {
        row.input.value = quantity <= min ? 0 : quantity - increment;
      }
      this.renderRow(row);
      this.updateSummary();
    }

    renderRow(row) {
      const { min, increment, max } = this.getRules(row);
      const available = Boolean(row.variant?.available) && max >= min;
      const quantity = this.normalizeQuantity(row, row.input.value);
      row.input.value = quantity;
      row.input.disabled = !available;
      row.input.min = '0';
      // Zero is always allowed even when the purchasable quantity starts above one.
      row.input.step = '1';
      if (max === Infinity) row.input.removeAttribute('max');
      else row.input.max = String(max);
      const decrease = row.element.querySelector('[data-accessory-decrease]');
      const increase = row.element.querySelector('[data-accessory-increase]');
      if (decrease) decrease.disabled = !available || quantity === 0;
      if (increase) increase.disabled = !available || (quantity > 0 && quantity + increment > max);
      row.element.querySelectorAll('[data-accessory-variant]').forEach((input) => {
        input.checked = input.value === String(row.variant?.id);
      });
      const price = row.element.querySelector('[data-accessory-price]');
      if (price) price.textContent = this.formatMoney(row.variant?.price || 0);
      const unavailable = row.element.querySelector('[data-accessory-unavailable]');
      if (unavailable) unavailable.hidden = available;
      const image = row.element.querySelector('[data-accessory-image]');
      if (image && row.variant?.image) {
        image.src = row.variant.image;
        image.removeAttribute('srcset');
        image.alt = row.variant.imageAlt || '';
      }
      row.element.classList.toggle('is-unavailable', !available);
    }

    getCartItems() {
      return (this.rows || []).reduce((items, row) => {
        const quantity = this.normalizeQuantity(row, row.input.value);
        if (quantity && row.variant?.id) items.push({ id: String(row.variant.id), quantity });
        return items;
      }, []);
    }

    updateSummary() {
      const total = (this.rows || []).reduce((sum, row) =>
        sum + this.normalizeQuantity(row, row.input.value) * (Number(row.variant?.price) || 0), 0);
      if (this.total) this.total.textContent = this.formatMoney(total);
      this.updatePaymentButtons();
    }

    updatePaymentButtons() {
      const productInfo = this.closest('product-info');
      const form = Array.from(productInfo?.querySelectorAll('form') || [])
        .find((item) => item.getAttribute('id') === this.dataset.productForm);
      if (!form) return;
      const hasAccessories = Array.from(productInfo.querySelectorAll('product-accessories'))
        .some((component) => component.dataset.productForm === form.getAttribute('id') && component.getCartItems?.().length);
      form.querySelectorAll('.shopify-payment-button').forEach((button) => {
        if (!this.paymentButtons.has(button)) this.paymentButtons.set(button, button.hidden);
        button.hidden = hasAccessories || this.paymentButtons.get(button);
      });
    }

    formatMoney(cents) {
      const formats = {
        amount: [2, ',', '.'],
        amount_no_decimals: [0, ',', '.'],
        amount_with_comma_separator: [2, '.', ','],
        amount_no_decimals_with_comma_separator: [0, '.', ','],
        amount_with_apostrophe_separator: [2, "'", '.'],
        amount_no_decimals_with_apostrophe_separator: [0, "'", '.'],
        amount_with_space_separator: [2, ' ', ','],
        amount_no_decimals_with_space_separator: [0, ' ', ','],
        amount_with_period_and_space_separator: [2, ' ', '.'],
      };
      const formatted = (this.dataset.moneyFormat || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
        const [precision, thousands, decimal] = formats[key] || formats.amount;
        const parts = (Number(cents) / 100).toFixed(precision).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts.join(decimal);
      });
      // Money formats may contain currency markup/entities; only their text is displayed.
      const template = document.createElement('template');
      template.innerHTML = formatted;
      return template.content.textContent;
    }

    disconnectedCallback() {
      this.paymentObserver?.disconnect();
      this.abortController?.abort();
      this.paymentButtons?.forEach((hidden, button) => { button.hidden = hidden; });
      this.paymentButtons?.clear();
      this.abortController = null;
      this.initialized = false;
    }
  }

  customElements.define('product-accessories', ProductAccessories);
})();
